/**
 * Toast notices.
 *
 * Rendered in an `aria-live="assertive"` region so a failed mutation is announced
 * rather than merely drawn. `assertive` rather than `polite` because a rollback
 * changes what the user just saw - a card that snapped back to its old position -
 * and a polite announcement would arrive after they had already started
 * wondering what happened.
 *
 * Errors carry the server's `requestId` when there is one. That is the whole
 * reason the API returns it: a user reporting a problem can quote eight
 * characters instead of describing a screen, and the log line is one search away.
 */

import type { ReactNode } from 'react';
import { useBoard } from '../state/BoardProvider.js';
import { Icon } from './ui.js';

const TONE_CLASS = {
  info: 'border-border bg-raised text-ink',
  success: 'border-success/40 bg-raised text-ink',
  error: 'border-danger/40 bg-raised text-ink',
} as const;

export function Notices(): ReactNode {
  const { state, actions } = useBoard();

  return (
    <div
      aria-live="assertive"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 left-1/2 z-40 flex w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-2"
    >
      {state.notices.map((notice) => (
        <div
          key={notice.id}
          className={`pointer-events-auto flex items-start gap-3 rounded-lg border px-3 py-2.5 shadow-lift ${TONE_CLASS[notice.tone]}`}
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm leading-snug">{notice.message}</p>
            {notice.requestId !== null && notice.requestId !== undefined && (
              <p className="mt-1 font-mono text-[11px] text-ink-subtle">request {notice.requestId}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => actions.dismissNotice(notice.id)}
            aria-label="Dismiss notification"
            className="rounded p-0.5 text-ink-subtle hover:bg-sunken hover:text-ink"
          >
            <Icon name="close" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
